// native
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

// addon
import { v4 as uuidv4 } from 'uuid';

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { ItemsDisplayService } from './items-display.service';

// models
import { IDataItemQueryParams, IDisplayItem, IArticleDataResponse, IArticle, IItem, IArticleAuthor, ScrollSession } from '../../models';

// constants
import { MAX_SEARCH_TERM, DEFAULT_QUERY_SIZE } from '../../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class ItemsWebService {

  constructor(
    private globalsService: GlobalsService,
    private apiService: ApiService,
    private itemsDisplayService: ItemsDisplayService
  ) { }

  search(params: IDataItemQueryParams, isScroll: boolean): Observable<IDisplayItem[]> {
    if (params.query && params.query.length > MAX_SEARCH_TERM) {
      return of();
    }

    const endpointUrl = `${this.globalsService.appConfig.environment.baseUrls.services}/articles/search`;
    const scrollSession = this.itemsDisplayService.scrollSessions.get(endpointUrl);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('size', DEFAULT_QUERY_SIZE.toString());

    if (isScroll && scrollSession && !scrollSession.expired()) {
      httpParams = httpParams.append('scroll_id', scrollSession.id);
    } else {
      if (params.query)
        httpParams = httpParams.append('query', params.query);

      httpParams = httpParams.append('sort_by', this.itemsDisplayService.sort);
      httpParams = httpParams.append('order', this.itemsDisplayService.order);
    }

    return this.apiService.get(endpointUrl, httpParams).pipe(
      tap((response: IArticleDataResponse) => this.itemsDisplayService.scrollSessions.set(endpointUrl, new ScrollSession(response.scroll_id))),

      map((response: IArticleDataResponse) => this.mapArticlesToItems(response.results || [])),
      map((items: IItem[]) => this.itemsDisplayService.parseItemsForDisplay(items || [])),

      tap((items: IDisplayItem[]) => this.itemsDisplayService.disableNextScrollSearchIfNeeded(items, isScroll)),
      map((items: IDisplayItem[]) => this.itemsDisplayService.handleScrollingItemPopulation(items, isScroll, scrollSession))
    );
  }

  private mapArticlesToItems(results: IArticle[]): IItem[] {
    return results.map((article: IArticle) => ({
      id: uuidv4(),
      type: null,
      article: this.mapDataToArticle(article),
      collection_group_id: null,
      collection_id: null,
      custom_type: null,
      item_type: null,
      seq: null,
      deleted: false,
      drm: false,
      files: [],
      import_data: {},
      primary_file_hash: null,
      primary_file_type: null,
      ext_ids: {
        pmid: article.pmid,
        doi: article.doi,
        pmcid: article.pmcid,
        gsid: article.gsid,
        arxiv: article.arxiv
      },
      user_data: {},
      custom_metadata: {}
    }));
  }

  private mapDataToArticle(article: IArticle): Partial<IArticle> {
    return ({
      journal_abbrev: article.journal_abbrev || '',
      pagination: article.pagination || '',
      authors: (<IArticleAuthor[]>article.authors)?.map(author => author.name),
      abstract: article.abstract || '',
      year: article.year || '',
      month: article.month || '',
      chapter: article.chapter || '',
      journal: article.journal || '',
      volume: article.volume || '',
      title: article.title || '',
      issue: article.issue || '',
      issn: article.issn || '',
      isbn: article.isbn || '',
      eissn: article.eissn || '',
      eisbn: article.eisbn || '',
      url: article.url || ''
    });
  };
}
